import { Button } from '@lmig/lmds-react-button';
import { Select, SelectOption } from '@lmig/lmds-react-select';
import { Heading } from '@lmig/lmds-react-typography';
import { useUserData } from 'Foundation/User/UserContext';
import { JSX, useContext, useEffect, useState } from 'react';
import { GetStateName } from 'Foundation/Common/StateHelper';
import './QuoteWidget.scss';
import { IQuoteContext, QuoteContext } from './QuoteContext';
import { Link } from '@lmig/lmds-react-link';
import { getClientList } from 'Foundation/Configurations/GlobalConfiguration';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lmig/lmds-react-modal';
import { GridCol, GridRow } from '@lmig/lmds-react-grid';
import { Notification } from '@lmig/lmds-react-notification';
import { isDesktopView, isMobileView, isTabletView } from 'Utilities/AppHelper';

export default function QuoteWidget() {
  const { userData } = useUserData();
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<IProduct>();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const quoteContext = useContext<IQuoteContext>(QuoteContext);
  const LSCphone: string = '888-451-8107';
  const EARTHQUAKE_NOTICE: JSX.Element = (
    <>
      For CA, OR, and WA earthquake quotes, contact the Licensed Sales Center at{' '}
      <Link href={`tel:${LSCphone}`}>{LSCphone}</Link>.
    </>
  );
  const [products, setProducts] = useState<IProduct[]>([]);

  const setUpdatedState = (state: string) => {
    if (states && states.find((s) => s === state)) {
      setSelectedState(state);
    }
  };

  const onQuoteButtonClick = () => {
    if (selectedProduct?.Name == 'Earthquake') {
      if (isMobileView() || isTabletView()) setNotificationIsOpen(true);
      else setModalIsOpen(true);
    } else if (selectedProduct?.Link && selectedProduct.Link !== '' && selectedState && selectedState !== '') {
      var link = selectedProduct.Link.replace('$STATE', selectedState);
      window.open(link);
    }
  };
  useEffect(() => {
    if (!quoteContext.products) quoteContext.setSending();
  }, []);

  useEffect(() => {
    const setStatesList = () => {
      if (quoteContext && quoteContext.products && quoteContext.products.length > 0) {
        let concatenatedStates = new Set<string>();
        quoteContext.products.forEach((p) => {
          p.States?.forEach((s) => concatenatedStates.add(s));
        });
        if (concatenatedStates.size > 0) {
          setStates(Array.from(concatenatedStates).sort());
        }
      }
    };
    if (quoteContext) {
      setStatesList();
      if (quoteContext?.products) {
        setProducts(quoteContext.products);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteContext]);

  useEffect(() => {
    if (userData?.state && userData.state !== '' && states.includes(userData.state)) setUpdatedState(userData.state);
    else if (states.length) setUpdatedState(states[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.state, states]);

  useEffect(() => {
    if (products && products.length > 0 && !selectedProduct) {
      setSelectedProduct(products.find((p: IProduct) => p.Name.toLowerCase().trim() === 'auto'));
    }
  }, [products]);

  function updateSelectedProduct(productHash: string) {
    let newProduct = products.find((product) => {
      return product.Name + product.Category === productHash;
    });
    setSelectedProduct(newProduct);
  }

  useEffect(() => {
    if (isMobileView() || isTabletView()) {
      if (selectedProduct?.Name === 'Earthquake') {
        setNotificationIsOpen(true);
      } else setNotificationIsOpen(false);
    }
  }, [selectedProduct, selectedState]);

  return userData ? (
    <>
      <div className="quote-card">
        {notificationIsOpen && (
          <Notification className="quote-ec-notification" alert="Earthquake quotes" highlightType="informative">
            {EARTHQUAKE_NOTICE}
          </Notification>
        )}
        <div className="quote-card-header">
          <Heading type="h4">Quote</Heading>
          <Link href={getClientList()} id="all-quotes-link" target="_blank" variant="standalone">
            See all quotes
          </Link>
        </div>
        <div>
          <Select
            isRequired
            labelVisual={`State`}
            value={selectedState}
            onChange={(event) => setUpdatedState(event.currentTarget.value)}
          >
            {quoteContext?.products &&
              states.map((state) => (
                <SelectOption key={GetStateName(state)} value={state}>
                  {GetStateName(state)}
                </SelectOption>
              ))}
          </Select>
          <Select
            disabled={!(products && products.length > 0)}
            isRequired
            value={selectedProduct?.Name ? selectedProduct.Name + selectedProduct.Category : ''}
            labelVisual={`Product`}
            onChange={(event) => updateSelectedProduct(event.currentTarget.value)}
          >
            {/*  */}
            {Object.entries(
              products.reduce((acc: Record<string, IProduct[]>, product) => {
                if (product.States?.includes(selectedState)) {
                  if (acc[product.Category]) {
                    acc[product.Category].push(product);
                  } else {
                    acc[product.Category] = [product];
                  }
                }
                return acc;
              }, {}),
            ).map(([category, products]) => (
              <optgroup label={category}>
                {products.map((product: IProduct) => (
                  <SelectOption value={product.Name + product.Category} key={product.Name}>
                    {product.Name}
                  </SelectOption>
                ))}
              </optgroup>
            ))}
          </Select>
          <Button
            dynamicWidth={true}
            variant="secondary"
            className="quote-button"
            disabled={selectedProduct ? (notificationIsOpen && !isDesktopView() ? true : false) : true}
            onClick={() => onQuoteButtonClick()}
          >
            Quote
          </Button>
          {notificationIsOpen && (
            <Button dynamicWidth={true} variant="secondary" className="call-for-quote-button" href={`tel:${LSCphone}`}>
              Call for a quote
            </Button>
          )}
        </div>
      </div>
      <Modal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)} size="small" className="quote-ec-modal">
        <ModalHeader>
          <Heading type="h3-light">Earthquake quotes</Heading>
        </ModalHeader>
        <ModalBody>
          <Heading type="h4-light">{EARTHQUAKE_NOTICE}</Heading>
        </ModalBody>
        <ModalFooter>
          <GridRow gutterSize="fixed-16" gutters>
            <GridCol base={12} md={6}>
              <Button dynamicWidth onClick={() => setModalIsOpen(false)}>
                Cancel
              </Button>
            </GridCol>
            <GridCol base={12} md={6}>
              <Button dynamicWidth variant="primary" href="tel:888-451-8107">
                Call for a quote
              </Button>
            </GridCol>
          </GridRow>
        </ModalFooter>
      </Modal>
    </>
  ) : (
    <></>
  );
}
